import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Button from './Button';
import { useAppSelector } from '../../store/hooks';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  totalCount: number;
  pageSize: number;
  showInfo?: boolean;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  totalCount,
  pageSize,
  showInfo = true
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  const getVisiblePages = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  if (totalPages <= 1) {
    return null;
  }

  return (
    <div className={`flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-0 px-4 py-3 border-t sm:px-6 ${
      theme === 'dark' 
        ? 'bg-gray-800 border-gray-700' 
        : 'bg-white border-gray-200'
    }`}>
      {/* Mobile info */}
      {showInfo && (
        <div className="flex-1 sm:hidden">
          <p className={`text-xs ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
          }`}>
            Showing <span className="font-medium">{startItem}</span> to{' '}
            <span className="font-medium">{endItem}</span> of{' '}
            <span className="font-medium">{totalCount}</span>
          </p>
        </div>
      )}

      {/* Desktop info */}
      {showInfo && (
        <div className="hidden sm:flex sm:flex-1 sm:items-center">
          <p className={`text-sm ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
          }`}>
            Showing <span className="font-medium">{startItem}</span> to{' '}
            <span className="font-medium">{endItem}</span> of{' '}
            <span className="font-medium">{totalCount}</span> results
          </p>
        </div>
      )}

      {/* Pagination controls */}
      <div className="flex items-center justify-center sm:justify-end gap-1 sm:gap-2 flex-wrap">
        {/* Previous button */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="flex items-center px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm"
        >
          <ChevronLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          <span className="ml-0.5 sm:ml-1 hidden sm:inline">Previous</span>
        </Button>

        {/* Page numbers - hide on very small screens */}
        <div className="hidden sm:flex items-center space-x-1">
          {getVisiblePages().map((page, index) => {
            if (page === '...') {
              return (
                <span
                  key={`dots-${index}`}
                  className={`px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}
                >
                  ...
                </span>
              );
            }

            const pageNumber = page as number;
            const isCurrentPage = pageNumber === currentPage;

            return (
              <button
                key={pageNumber}
                onClick={() => onPageChange(pageNumber)}
                className={`px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-md ${
                  isCurrentPage
                    ? theme === 'dark'
                      ? 'bg-[#33FF00] text-black'
                      : 'bg-primary-600 text-white'
                    : theme === 'dark'
                      ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                {pageNumber}
              </button>
            );
          })}
        </div>

        {/* Current page indicator for mobile */}
        <div className="sm:hidden flex items-center">
          <span className={`px-2.5 py-1.5 text-xs font-medium rounded-md ${
            theme === 'dark'
              ? 'bg-[#33FF00] text-black'
              : 'bg-primary-600 text-white'
          }`}>
            {currentPage} / {totalPages}
          </span>
        </div>

        {/* Next button */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="flex items-center px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm"
        >
          <span className="mr-0.5 sm:mr-1 hidden sm:inline">Next</span>
          <ChevronRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
        </Button>
      </div>
    </div>
  );
};

export default Pagination;
